import React from 'react';
import { Mail, Star, Send, FileText, Trash2, AlertCircle, Archive, Inbox, Plus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface EmptyStateProps {
  folderName: string;
  onCompose?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ folderName, onCompose }) => {
  const navigate = useNavigate();
  // Get the route for the current folder
  const getFolderRoute = (folder: string): string => {
    // Format folder name to match API expectations
    const formattedFolder = folder === 'inbox' ? 'INBOX' : folder;
    
    const folderRoutes: { [key: string]: string } = {
      'INBOX': '/inbox',
      'SENT': '/sent',
      'STARRED': '/starred',
      'DRAFTS': '/drafts',
      'ARCHIVE': '/archive',
      'SPAM': '/spam',
      'TRASH': '/trash',
      'JUNK': '/junk'
    };
    return folderRoutes[formattedFolder.toUpperCase()] || `/folder/${formattedFolder}`;
  };

  const getFolderIcon = () => {
    const iconSize = 64;
    const iconColor = '#d1d5db';
    
    switch (folderName.toUpperCase()) {
      case 'INBOX':
        return <Inbox size={iconSize} color={iconColor} />;
      case 'STARRED':
        return <Star size={iconSize} color={iconColor} />;
      case 'SENT':
        return <Send size={iconSize} color={iconColor} />;
      case 'DRAFTS':
        return <FileText size={iconSize} color={iconColor} />;
      case 'TRASH':
        return <Trash2 size={iconSize} color={iconColor} />;
      case 'SPAM':
      case 'JUNK':
        return <AlertCircle size={iconSize} color={iconColor} />;
      case 'ARCHIVE':
        return <Archive size={iconSize} color={iconColor} />;
      default:
        return <Mail size={iconSize} color={iconColor} />;
    }
  };

  const getEmptyMessage = () => {
    switch (folderName.toUpperCase()) {
      case 'INBOX':
        return {
          title: 'Your inbox is empty',
          description: 'No new messages yet. When you receive emails, they\'ll appear here.',
          action: 'Compose a message'
        };
      case 'STARRED':
        return {
          title: 'No starred messages',
          description: 'Star important emails to find them quickly. Click the star icon next to any message.',
          action: 'Go to inbox'
        };
      case 'SENT':
        return {
          title: 'No sent messages',
          description: 'Messages you send will appear here. Start by composing a new email.',
          action: 'Compose a message'
        };
      case 'DRAFTS':
        return {
          title: 'No drafts',
          description: 'Save emails as drafts to finish them later. Start writing a new message.',
          action: 'Compose a message'
        };
      case 'TRASH':
        return {
          title: 'Trash is empty',
          description: 'Deleted messages will be moved here. They\'ll be permanently deleted after 30 days.',
          action: 'Go to inbox'
        };
      case 'SPAM':
      case 'JUNK':
        return {
          title: 'No spam messages',
          description: 'Great! Your spam filter is working well. No unwanted messages found.',
          action: 'Go to inbox'
        };
      case 'ARCHIVE':
        return {
          title: 'No archived messages',
          description: 'Archive important emails to keep your inbox organized.',
          action: 'Go to inbox'
        };
      default:
        return {
          title: 'No messages found',
          description: 'This folder is empty. Check other folders or compose a new message.',
          action: 'Compose a message'
        };
    }
  };

  const message = getEmptyMessage();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      width: '100%',
      minHeight: '90vh',
      margin: '20px',
      padding: '40px 20px',
      // background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
      background: '#ffffff80',
      borderRadius: '12px',
      border: '1px solid rgb(210, 213, 220)'
    }}>
      {/* Icon */}
      <div style={{
        marginBottom: '24px',
        opacity: 0.6
      }}>
        {getFolderIcon()}
      </div>

      {/* Title */}
      <h3 style={{
        fontSize: '20px',
        fontWeight: '600',
        color: '#374151',
        margin: '0 0 12px 0',
        textAlign: 'center'
      }}>
        {message.title}
      </h3>
      
      {/* Description */}
      <p style={{
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 32px 0',
        textAlign: 'center',
        maxWidth: '400px',
        lineHeight: '1.5'
      }}>
        {message.description}
      </p>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px' }}>
        {/* Back Button */}
        <button
          onClick={() => {
            // Navigate to the folder with page=1 and reset unread filter
            navigate({
              pathname: getFolderRoute(folderName),
              search: '?page=1&isUnread=false'
            });
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e5e7eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {/* Action Button */}
        {onCompose && (
          <button
            onClick={onCompose}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#8D00E4',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(141, 0, 228, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#7a00cc';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(141, 0, 228, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#8D00E4';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(141, 0, 228, 0.1)';
            }}
          >
            <Plus size={16} />
            {message.action}
          </button>
        )}
      </div>

      {/* Decorative Elements */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(141, 0, 228, 0.1) 0%, rgba(141, 0, 228, 0.05) 100%)',
        opacity: 0.5
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(141, 0, 228, 0.08) 0%, rgba(141, 0, 228, 0.03) 100%)',
        opacity: 0.4
      }} />
    </div>
  );
};

export default EmptyState; 